import {MenuPage} from "../../framework/MenuPage";
import {AbstractViewModel} from "../../framework/AbstractViewModel";
import {NumberFormatter} from "../../util/NumberFormatter";
import {RAID_STATUS} from "../../constants/RaidStatus";
import {RaidStatusListener} from "../../grass_listeners/RaidStatusListener";
import {MAP_CONTAINER_IDS} from "../../constants/MapConstants";
import {PlanetRaidFactory} from "../../factories/PlanetRaidFactory";
import {GenericResourceComponent} from "../components/GenericResourceComponent";
import {PLAYER_TYPES} from "../../constants/PlayerTypes";

export class PreviewViewModel extends AbstractViewModel {

  /**
   * @param {GameState} gameState
   * @param {GuildAPI} guildAPI
   * @param {FleetManager} fleetManager
   * @param {GrassManager} grassManager
   * @param {RaidManager} raidManager
   * @param {MapManager} mapManager
   * @param {string} planet_id
   * @param {number} planet_undiscovered_ore
   * @param {string} defender_id
   * @param {number} defender_ore
   * @param {string|null} attacker_id
   */
  constructor(
    gameState,
    guildAPI,
    fleetManager,
    grassManager,
    raidManager,
    mapManager,
    planet_id,
    planet_undiscovered_ore,
    defender_id,
    defender_ore,
    attacker_id = null
  ) {
    super();
    this.gameState = gameState;
    this.guildAPI = guildAPI;
    this.fleetManager = fleetManager;
    this.grassManager = grassManager;
    this.raidManager = raidManager;
    this.mapManager = mapManager;
    this.planet_id = planet_id;
    this.planet_undiscovered_ore = planet_undiscovered_ore;
    this.defender_id = defender_id;
    this.defender_ore = defender_ore;
    this.attacker_id = attacker_id;
    this.numberFormatter = new NumberFormatter();
    this.planetRaidFactory = new PlanetRaidFactory();
    this.launchFleetBtnId = 'launch-fleet';
    this.undiscoveredOreId = 'preview-planet-undiscovered-ore';
    this.alphaOreId = 'preview-defender-ore-mined';
  }

  initPageCode() {
    if (this.attacker === null) {
      return;
    }

    const launchFleetBtnElm = document.getElementById(this.launchFleetBtnId);

    if (!launchFleetBtnElm) {
      return;
    }

    launchFleetBtnElm.addEventListener('click', () => {

      // Safeguard in case the player doesn't have a command ship by the time they attempt to launch.
      if (!this.gameState.keyPlayers[PLAYER_TYPES.PLAYER].isCommandStructAlive()) {
        MenuPage.router.goto('Fleet', 'index');
        return;
      }

      const planetRaid = this.planetRaidFactory.make({
        fleet_id: this.gameState.keyPlayers[PLAYER_TYPES.PLAYER].player.fleet_id,
        planet_id: this.planet_id,
        planet_owner: this.defender_id,
        status: RAID_STATUS.REQUESTED
      });

      this.gameState.setRaidPlanetRaidInfo(planetRaid);

      MenuPage.router.goto('Fleet', 'index');

      this.grassManager.registerListener(new RaidStatusListener(this.gameState, this.raidManager, this.mapManager));
      this.fleetManager.moveFleet(this.gameState.keyPlayers[PLAYER_TYPES.RAID_ENEMY].planetRaidInfo.planet_id).then();
    });
  }

  /**
   * @param {Player} player
   * @return {string}
   */
  renderUsernameHTML(player) {
    let tag = player.tag
      ? `<span class="sui-mod-secondary">[${player.tag}]</span>`
      : '';
    let username = player.username
      ? player.username
      : `PID #${player.id}`;


    return `<span>${tag} ${username}</span>`;
  }

  render() {
    Promise.all([
      this.guildAPI.getPlanet(this.planet_id),
      this.guildAPI.getPlayer(this.defender_id),
      this.guildAPI.getStructsByPlayerId(this.defender_id),
      this.guildAPI.getFleetByPlayerId(this.defender_id),
      (async () => (this.attacker_id === null)
            ? null
            : await this.guildAPI.getPlayer(this.attacker_id)
      )(),
      (async () => (this.attacker_id === null)
          ? []
          : await this.guildAPI.getStructsByPlayerId(this.attacker_id)
      )(),
      (async () => (this.attacker_id === null)
          ? null
          : await this.guildAPI.getFleetByPlayerId(this.attacker_id)
      )(),
    ]).then(
      ([
        planet,
        defender,
        defenderStructs,
        defenderFleet,
        attacker,
        attackerStructs,
        attackerFleet,
      ]) => {
        this.mapManager.configurePreviewMap(planet, defender, attacker, defenderFleet, attackerFleet);
        this.gameState.setPreviewDefenderStructs(defenderStructs);
        this.gameState.setPreviewAttackerStructs(attackerStructs);
        this.mapManager.showMap(MAP_CONTAINER_IDS.PREVIEW);
        this.gameState.previewMap.render();

        const genericResourceComponent = new GenericResourceComponent(MenuPage.gameState);

        const headerResourcesHTML = `
          <div class="sui-page-header-resources">
            ${
              genericResourceComponent.renderHTML(
                this.undiscoveredOreId,
                'sui-icon-undiscovered-ore',
                'Undiscovered Ore',
                this.numberFormatter.format(this.planet_undiscovered_ore)
              )
            }
            ${
              genericResourceComponent.renderHTML(
                this.alphaOreId,
                'sui-icon-alpha-ore',
                'Ore Mined',
                this.numberFormatter.format(this.defender_ore)
              )
            }
          </div>
        `;

        MenuPage.enablePageTemplate(
          MenuPage.navItemFleetId,
          false,
          true,
          true,
          headerResourcesHTML
        );

        MenuPage.setPageTemplateHeaderBtn(this.renderUsernameHTML(defender), true, () => {
          MenuPage.router.back();
        });

        const launchFleetBtn = (
            this.attacker_id === null
            && this.gameState.keyPlayers[PLAYER_TYPES.PLAYER].isCommandStructAlive()
        )
          ? `
            <div class="preview-map-btn-container">
              <a 
                id="${this.launchFleetBtnId}"
                class="sui-screen-btn sui-mod-primary"
                href="javascript: void(0)" 
              >Launch Fleet</a>
            </div>
          `
          : ``;

        MenuPage.setPageTemplateContent(`${launchFleetBtn}`);

        MenuPage.hideAndClearDialoguePanel();

        this.initPageCode();

        MenuPage.open();
      }
    );
  }
}
